<template>
  <div v-bind="rootAttrs">
    <FloatLabel variant="on" class="relative transition-colors duration-200">
      <DatePicker
        v-model="calendarValue"
        :inputId="id"
        :name="name"
        :readonly="readonly"
        :disabled="disabled"
        :minDate="minDate"
        :maxDate="maxDate"
        :showTime="isDateTime"
        :timeOnly="isTime"
        :dateFormat="dateFormat"
        :class="[width, height]"
        :required="required"
        :manualInput="true"
        :keepInvalid="false"
        :showOnFocus="true"
        :showIcon="false"
        @focus="handleFocus"
        @blur="handleBlur"
        @date-select="handleDateSelect"
        @input="handleInput"
        @clear="handleClear"
        :inputProps="{
          pattern: getInputPattern(),
          readonly: true,
        }"
        :inputClass="[
          '!w-full bg-transparent border-2 rounded-lg px-3.5 !py-[16.5px] !text-labelColor !font-just text-base cursor-pointer',
          inputClass,
          inputClasses,
        ]"
        data-test-id="date-picker"
      />
      <label
        :for="id"
        class="absolute left-2 -top-2 !font-just !text-base pointer-events-none transition-all duration-200 flex gap-1"
        :class="[labelClasses, labelClass]"
        :data-test-id="`${id}-label`"
      >
        <template v-if="required">
          {{ label }}<span v-if="focused || !calendarValue">*</span>
        </template>
        <template v-else>
          {{ label }}
        </template>
      </label>
    </FloatLabel>
    <Message
      v-if="invalid && errorMessage"
      severity="error"
      size="small"
      variant="simple"
      :class="{
        '!text-error  !px-[10px] !mt-[3.84px] !font-robo !font-400 !text-[15.36px]':
          invalid,
      }"
      :data-test-id="`${id}-errorMessage`"
    >
      {{ errorMessage }}
    </Message>
  </div>
</template>

<script setup>
// Stop parent fallthrough attrs (e.g. a tabindex="0" from CustomField) from
// landing on the wrapper <div> and creating a second tab stop in front of the
// DatePicker's own input. We intentionally do NOT re-forward $attrs to the
// DatePicker: it routes input attrs through :inputProps and spreads the rest
// onto its root span, where a tabindex would just recreate the extra stop.
// (Consequence: parent-passed listeners/class aren't forwarded here; date
// fields aren't encrypted, so the CustomField focus/click handlers are no-ops.
// If a date field ever needs them, forward via :inputProps instead.)
defineOptions({ inheritAttrs: false });

import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { datePatterns } from "@/utils/validation";
import { useControlAttrs } from "@/composables/useControlAttrs";

const { locale } = useI18n();

// Put the parent's class/style (grid col-span, width) on the root. The
// DatePicker routes its own input attrs via :inputProps; date fields aren't
// encrypted, so the parent's listeners aren't needed on the control.
const { rootAttrs } = useControlAttrs();

const props = defineProps({
  modelValue: [String, Date],
  type: { type: String, default: "date" },
  label: String,
  labelClass: {},
  inputClass: String,
  id: String,
  name: { type: String, default: "datetime" },
  required: Boolean,
  errorMessage: String,
  invalid: Boolean,
  width: { type: String, default: "w-full" },
  height: { type: String, default: "h-auto" },
  readonly: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  min: {},
  max: {},
});

const emit = defineEmits(["update:modelValue", "input", "blur"]);

const focused = ref(false);

const parseTimeString = (timeString) => {
  const today = new Date();
  const [hours, minutes] = timeString.split(":").map(Number);
  today.setHours(hours, minutes, 0, 0);
  return isNaN(today.getTime()) ? null : today;
};

const parseDateString = (dateString) => {
  const [year, month, day] = dateString.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const parseDateTimeString = (dateTimeString) => {
  const parsed = new Date(dateTimeString);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const parseGenericString = (value) => {
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const parseStringByType = (value) => {
  if (props.type === "time" && value.match(/^\d{2}:\d{2}$/)) {
    return parseTimeString(value);
  }

  if (props.type === "date" && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return parseDateString(value);
  }

  if (
    props.type === "datetime-local" &&
    value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)
  ) {
    return parseDateTimeString(value);
  }

  return parseGenericString(value);
};

const parseDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string") {
    return parseStringByType(value);
  }

  return null;
};

const formatOutput = (date) => {
  if (!date || isNaN(date.getTime())) return "";

  try {
    if (props.type === "date") {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } else if (props.type === "datetime-local") {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } else if (props.type === "time") {
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    }
  } catch (error) {
    return "";
  }

  return "";
};

const calendarValue = ref(parseDate(props.modelValue));

watch(
  () => props.modelValue,
  (newVal) => {
    const parsedDate = parseDate(newVal);
    if (calendarValue.value?.getTime() !== parsedDate?.getTime()) {
      calendarValue.value = parsedDate;
    }
  }
);

watch(
  calendarValue,
  (newVal) => {
    const formattedValue = formatOutput(newVal);
    if (formattedValue !== props.modelValue) {
      emit("update:modelValue", formattedValue);
      emit("input", newVal);
    }
  }
);

const handleFocus = () => {
  focused.value = true;
};

const handleBlur = (event) => {
  focused.value = false;
  emit("blur", event);
};

const handleDateSelect = (value) => {
  calendarValue.value = value;
};

const handleInput = (value) => {
  // Handled by watcher
};

const handleClear = () => {
  calendarValue.value = null;
  emit("update:modelValue", "");
  emit("input", null);
};

const getInputPattern = () => {
  switch (props.type) {
    case "date":
      return datePatterns.date;
    case "datetime-local":
      return datePatterns.dateTime;
    case "time":
      return datePatterns.time;
    default:
      return datePatterns.date;
  }
};

const inputClasses = computed(() => ({
  "!border-deepSky": (focused.value || calendarValue.value) && !props.invalid,
  "!border-softGray": !focused.value && !calendarValue.value && !props.invalid,
  "!border-error": props.invalid,
}));

const labelClasses = computed(() => ({
  "!text-deepSky": (focused.value || calendarValue.value) && !props.invalid,
  "!text-greyBlue": !focused.value && !calendarValue.value && !props.invalid,
  "!text-error": props.invalid,
  "float-label": focused.value || calendarValue.value,
}));

const isDateTime = computed(() => props.type === "datetime-local");
const isTime = computed(() => props.type === "time");

// No placeholder: the FloatLabel acts as the field's resting label (it floats up
// on focus/fill), matching BaseInput. A persistent placeholder would overlap it.
const dateFormat = computed(() => {
  if (isTime.value) return undefined;
  
  // Use different date formats based on locale
  // English (en): MM/DD/YYYY
  // Portuguese (pt): DD/MM/YYYY
  const format = locale.value === 'pt' ? 'dd/mm/yy' : 'mm/dd/yy';
  
  if (isDateTime.value) return format;
  return format;
});

const minDate = computed(() => (props.min ? parseDate(props.min) : undefined));
const maxDate = computed(() => (props.max ? parseDate(props.max) : undefined));
</script>

<style scoped>
:deep(.p-datepicker-input) {
  cursor: pointer !important;
}

:deep(.p-datepicker-input:focus) {
  outline: none;
}
</style>
